'use client';

import { useLDClient } from 'launchdarkly-react-client-sdk';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { LDUser } from '@/models';

import { trackHubSpotPageView } from './HubSpotPixel';
import { useEngagementSignals } from './useEngagementSignals';
import { useLogger } from './useLogger';

// Mounted once in the root layout; fires on initial load and each route change (search params ignored)
export const PageViewTracker = () => {
  const pathname = usePathname();
  const ldClient = useLDClient();
  const { addError } = useLogger();

  // addError and ldClient change reference shortly after mount (SDK init), re-running
  // this effect with the same pathname; dedupe so each route is tracked exactly once.
  const prevPathname = useRef<string | null>(null);

  // Record a passive "real human engaged" signal so pre-warm loads can be excluded from conversion (CONS-2436)
  useEngagementSignals();

  useEffect(() => {
    // Wait for the LD context before tracking so the tester check is accurate and
    // FacebookPixel's init has been queued first.
    const context = ldClient?.getContext() as LDUser | undefined;

    if (window.E2E_TEST || prevPathname.current === pathname || !context || context.isTester) {
      return;
    }

    prevPathname.current = pathname;

    try {
      trackHubSpotPageView(pathname);
    } catch (err) {
      addError(err, { component: 'PageViewTracker', pathname });
    }
  }, [addError, ldClient, pathname]);

  return null;
};
